/** A live dataset's versions to run on, and which one the run uses. */

import { useState } from "react";
import type { Dataset, DatasetVersion } from "@/features/datasets/types";
import { useApiQuery } from "@/lib/query";
import type { VersionChoice } from "./types";

/** The same query as `useDataset`, off until a live dataset is picked. */
export const useLiveVersions = (datasetId: string | null, enabled: boolean) =>
  useApiQuery<Dataset>(`/datasets/${datasetId}`, {}, { enabled: enabled && !!datasetId });

/**
 * Fresh traces, pulled when the run starts, unless the person picks a version.
 * A duplicate starts on its original's version, so the copy runs on the same traces.
 */
export function useVersionChoice(args: {
  datasetId: string | null;
  live: boolean;
  /** A duplicate's version, picked first. */
  original: number | null | undefined;
  /** What was picked before a reload. */
  kept?: KeptVersion | undefined;
}) {
  const query = useLiveVersions(args.datasetId, args.live);
  const versions = (args.live ? (query.data?.versions ?? []) : []) as DatasetVersion[];
  // A pick belongs to the dataset it was made on.
  const [pick, setPick] = useState<{ datasetId: string | null; choice: VersionChoice } | null>(() =>
    args.kept?.choice != null ? { datasetId: args.kept.datasetId, choice: args.kept.choice } : null,
  );
  const picked = pick?.datasetId === args.datasetId ? pick.choice : null;
  const [next, setNext] = useState(() => !!args.kept?.next);

  // A duplicate's version comes from its original, never from the list: until the list loads
  // (or if it fails) the copy still runs on the same version, not on fresh traces.
  const choice: VersionChoice = picked ?? args.original ?? "fresh";
  const version = choice === "fresh" ? null : choice;
  const chosen = versions.find((p) => p.version === version);
  // The server continues after its newest pull, not after an upload or an edit.
  const latest = versions.find((p) => p.origin === "trace_pull")?.version ?? null;
  return {
    /** The dataset's versions, newest first, with their loading and error state. */
    versions,
    query,
    /**
     * Start waits for the list when it runs on a version, or pulls next (the list says
     * where to continue from); plain fresh traces need none.
     */
    settled: !args.live || (version == null && !next) || query.isSuccess,
    choice,
    pick: (c: VersionChoice) => setPick({ datasetId: args.datasetId, choice: c }),
    /** The version number the run uses; null for fresh traces. */
    version,
    /** That version's details, once the list has loaded. */
    chosen,
    /** The version is no longer in the dataset's list. */
    missing: version != null && query.isSuccess && !chosen,
    fresh: version == null,
    /** Fresh traces continue after the latest pull's last trace (`continue_from_latest`). */
    next: version == null && next && latest != null,
    setNext,
    /** The newest pulled version's number, which "Pull next" continues after. */
    latest,
    original: args.original ?? null,
    /** What to keep across a reload. */
    kept: { datasetId: args.datasetId, choice: picked, next } satisfies KeptVersion,
  };
}

/** A live dataset's version pick and "Pull next", as the tab keeps them. */
export interface KeptVersion {
  datasetId: string | null;
  /** Null while nothing was picked (fresh, or a duplicate's original). */
  choice: VersionChoice | null;
  next: boolean;
}

export type LiveChoice = ReturnType<typeof useVersionChoice>;

/** Why the run cannot start yet: its live dataset's versions have not loaded. */
export function versionBlocker(live: LiveChoice): string | null {
  if (live.missing) return `v${live.version} is no longer in this dataset. Pick another version on Dataset.`;
  if (live.settled) return null;
  return live.query.isError
    ? "Couldn't load the dataset's versions. Go back to Dataset to try again."
    : "Loading the dataset's versions…";
}
